import { useCallback, useRef, useState, useEffect } from 'react';

interface UseScrollEndOptions {
	offset?: number;
	onReachEnd?: () => void;
}

export const useScrollEnd = <T extends HTMLElement = HTMLDivElement>(
	options: UseScrollEndOptions = {}
) => {
	const { offset = 60, onReachEnd } = options;
	const containerRef = useRef<T>(null);
	const [hasReachedEnd, setHasReachedEnd] = useState(false);

	const checkScrollEnd = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		const { scrollTop, scrollHeight, clientHeight } = container;
		const isAtEnd = scrollTop + clientHeight >= scrollHeight - offset;

		if (isAtEnd && !hasReachedEnd) {
			setHasReachedEnd(true);
			onReachEnd?.();
		} else if (!isAtEnd && hasReachedEnd) {
			setHasReachedEnd(false);
		}
	}, [hasReachedEnd, offset, onReachEnd]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		container.addEventListener('scroll', checkScrollEnd);
		checkScrollEnd(); // check inicial

		return () => container.removeEventListener('scroll', checkScrollEnd);
	}, [checkScrollEnd]);

	return {
		containerRef,
		hasReachedEnd,
		checkScrollEnd,
		setHasReachedEnd,
	};
};
